'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { usePortalContainer } from '@church/nextjs-ui/bottom-sheet';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  HandHeart,
  LogIn,
  Package,
  RotateCcw,
  UserPlus,
  X,
} from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type {
  CareOffer,
  FinderOpportunity,
  FormAnswers,
  HouseholdMember,
  MyServeResponse,
  ServeForm,
} from '@/lib/types';
import {
  ResponseError,
  askAboutCpp,
  cancelShifts,
  confirmServe,
  fetchCppCleared,
  fetchForm,
  fetchHousehold,
  fetchMyCare,
  submitResponse,
} from '@/lib/api';
import {
  backFromSignIn,
  getMpWidgetToken,
  onMpTokenChange,
  signInAndReturn,
  signInThenOpen,
} from '@/lib/campus';
import { campusLabel, fmtDay, parseMp } from '@/lib/format';
import { Avatar } from './Avatar';
import { CampusIcon } from './CampusIcon';
import { CarePanel, type CareDone } from './CarePanel';
import { RegisteredCurtain } from './RegisteredCurtain';
import { FormFields, missingRequired } from './FormFields';
import { serveShift } from './ServeCard';
import {
  BLEED,
  PERSON_BAR,
  STICK_BG,
  TeamPicker,
  hasTeams,
  serviceTime,
  teamSummaries,
} from './TeamPicker';
import { useBottomDock } from '@/lib/bottom-dock';

/**
 * WHO, ON A COLLECTION: THE EVENT PAGE'S REGISTRATION, FOR WAYS TO HELP.
 *
 * Copied from the event-details widget, not merely like it (SignUp.tsx,
 * RegistrationTypes.tsx, RegistrationSheet.tsx):
 *
 *   The Who card is the household, a checkbox each, and under each name the
 *   ways in as pills: Volunteer (with the roles it covers), or, when the
 *   roles carry Serve Teams, the teams themselves (Welcome, Kids...), since
 *   on a serve page every way in is volunteering; and the care the
 *   Collection offers ("Thanksgiving Box"), plus a check for anything they
 *   already hold. Next opens the sheet.
 *
 *   The sheet wears the Collection's band and a step rail, and walks person
 *   by person, the way "How is Colton coming? 1 of 2" does:
 *
 *     Way in    "How is Colton taking part?" Volunteer / Thanksgiving Box.
 *               With teams: "How is Colton helping?" a multiselect of the
 *               teams with Select all; the next step shows every ticked
 *               team's tables, and Back changes the teams.
 *               A tap ticks the card and moves to the next person.
 *     Help      "What is Colton helping with?" Their campus (with its art),
 *               then that campus's roles as shift rows, more than one allowed.
 *     Details   "A few questions for Colton", only for a role with a form.
 *     Review    who is doing what, a note, Sign up.
 *     Box       the household's box: the places, then the request.
 *
 *   Back on the left, a hint and "Next: Sarah" on the right.
 *
 * A role's form is about one person, so a role with a form goes once per
 * person; without one, once for everybody on it.
 */

const CHURCH_WIDE = 1;

type Way = 'help' | 'care';
type Step = 'choose' | 'who' | 'way' | 'help' | 'details' | 'review' | 'done' | 'box';

const STEP_NAME: Record<Exclude<Step, 'choose' | 'done'>, string> = {
  who: 'Who',
  way: 'Registration',
  help: 'Volunteer',
  details: 'Questions',
  review: 'Review',
  box: 'Box',
};

const ROW =
  'card lift flex h-full w-full items-center gap-3 p-4 text-left transition [&]:cursor-pointer';

/** "Lake Orion | Thanksgiving Box | Box Inventory" in Thanksgiving Boxes → "Box Inventory". */
export function roleName(o: FinderOpportunity, collectionTitle: string): string {
  const campus = o.congregationName.replace(/\s+campus$/i, '').trim();
  const words = collectionTitle.trim().replace(/e?s$/i, '');
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let name = o.title
    .replace(new RegExp(`\\b${esc(campus)}\\b`, 'gi'), '')
    .replace(new RegExp(`\\b${esc(words)}(e?s)?\\b`, 'gi'), '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' · ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!name) name = 'Volunteer';
  return name.charAt(0).toUpperCase() + name.slice(1);
}

const holds = (mine: MyServeResponse[], contactId: number, roleId: number) =>
  mine.some(
    (r) =>
      r.contactId === contactId &&
      r.opportunityId === roleId &&
      (r.status === 'pending' || r.status === 'placed')
  );

const firstName = (m: HouseholdMember) => m.name.split(' ')[0];

export interface WhoPreset {
  /** A role tapped somewhere on the page: the viewer goes on it */
  roleId?: number;
  /** Or a step to open on ('box' for a care link, 'choose' for the phone dock) */
  page?: 'choose' | 'box';
  /**
   * Or a link on the host page (the sheet layout): 'help' opens on who is
   * helping, then campus and roles; 'care' on the box.
   */
  side?: Way;
  /** Changes on every tap, so tapping the same thing twice opens it twice */
  nonce: number;
}

export function CollectionWho({
  apiBaseUrl,
  collection,
  roles,
  defaultCampusId = null,
  mine,
  onSent,
  accentStyle,
  preset,
  careOffers = [],
  careLabel,
  onOpenChange,
  sheetOnly = false,
}: {
  apiBaseUrl: string;
  collection: FinderOpportunity;
  /** Every way to help under the Collection, every campus (signed up for here, not a partner's) */
  roles: FinderOpportunity[];
  /** The visitor's campus (the campus picker's): where each volunteer starts */
  defaultCampusId?: number | null;
  mine: MyServeResponse[];
  onSent: () => void;
  accentStyle?: React.CSSProperties;
  preset?: WhoPreset | null;
  /** The care this Collection offers, asked for in the same sheet */
  careOffers?: CareOffer[];
  /** The care type's way in ("Receive a box") */
  careLabel?: string | null;
  /** The sheet opened or closed (the phone dock stands down while it is open) */
  onOpenChange?: (open: boolean) => void;
  /** Only the sheet, opened by the host page's own links (the sheet layout) */
  sheetOnly?: boolean;
}) {
  const [token, setToken] = useState<string | null>(() => getMpWidgetToken());
  const [members, setMembers] = useState<HouseholdMember[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [step, setStep] = useState<Step | null>(null);
  const [startRole, setStartRole] = useState<number | null>(null);
  // Opened from a page's Serve link: who is helping comes first.
  const [pickFirst, setPickFirst] = useState(false);
  const hasCare = careOffers.length > 0 && !!careLabel;
  // The way in's name: the care type's Way In Label ("Receive a box").
  const careName = careLabel ?? null;

  useEffect(() => onMpTokenChange(setToken), []);

  // Who needs a signed-in household: signed out, go straight to sign-in and
  // come back to this step, rather than stopping on a lone Sign in button.
  // Back from sign-in and still signed out (cancelled), the button stays.
  useEffect(() => {
    if (step === 'who' && !token && !backFromSignIn()) signInThenOpen('#signup-serve');
  }, [step, token]);
  useEffect(() => onOpenChange?.(step !== null), [step, onOpenChange]);

  // The household's box in this Collection, if it has one: care is one per
  // household, so once asked for it shows as done on everyone and is not
  // offered again.
  const [householdBox, setHouseholdBox] = useState<HouseholdBox | null>(null);
  useEffect(() => {
    setHouseholdBox(null);
    if (!token || !careOffers.length) return;
    const controller = new AbortController();
    fetchMyCare(apiBaseUrl, controller.signal)
      .then((reqs) => {
        if (controller.signal.aborted) return;
        const hit = reqs?.find((r) => careOffers.some((o) => o.id === r.offerId));
        if (hit) setHouseholdBox({ offerId: hit.offerId, place: hit.place });
      })
      .catch(() => {});
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, token, careOffers.length]);
  useEffect(() => {
    if (!token) {
      setMembers(null);
      return;
    }
    const controller = new AbortController();
    fetchHousehold(apiBaseUrl, controller.signal)
      .then((m) => !controller.signal.aborted && setMembers(m ?? []))
      .catch(() => !controller.signal.aborted && setMembers([]));
    return () => controller.abort();
  }, [apiBaseUrl, token]);

  const self = members?.find((m) => m.isSelf) ?? null;
  // On a serve page with Serve Teams, the teams are the registration types.
  const teams = useMemo(() => (hasTeams(roles) ? teamSummaries(roles) : []), [roles]);
  // The roles Volunteer covers, for its pill and its card: one name each.
  const roleNames = useMemo(
    () => [...new Set(roles.map((o) => roleName(o, collection.title)))],
    [roles, collection.title]
  );

  useEffect(() => {
    if (!preset) return;
    setPickFirst(preset.side === 'help');
    if (preset.side) {
      setStep(preset.side === 'care' ? 'box' : 'who');
      return;
    }
    if (preset.roleId === undefined) {
      setStep(
        preset.page ?? (selected.length ? (hasCare || teams.length ? 'way' : 'help') : 'choose')
      );
      return;
    }
    if (!token) {
      signInAndReturn();
      return;
    }
    if (!self) return;
    setSelected((cur) => (cur.includes(self.contactId) ? cur : [...cur, self.contactId]));
    setStartRole(preset.roleId);
    setStep('help');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.nonce, self?.contactId]);

  const toggle = (contactId: number) =>
    setSelected((cur) =>
      cur.includes(contactId) ? cur.filter((id) => id !== contactId) : [...cur, contactId]
    );
  const chosen = (members ?? []).filter((m) => selected.includes(m.contactId));
  const { slot: dockSlot, dock } = useBottomDock(chosen.length > 0);

  // A box asked for: the curtain, as an event's registration ends, filing
  // itself into this card, and a tick on each person it is for.
  const whoList = useRef<HTMLDivElement>(null);
  const overlayHost = usePortalContainer();
  const [boxDone, setBoxDone] = useState<CareDone | null>(null);
  const onBoxed = (done: CareDone) => {
    setHouseholdBox({ offerId: done.offerId, place: done.title.split(' · ')[0] });
    setBoxDone(done);
  };

  return (
    <>
      {!sheetOnly && (
        <>
          <span className="font-[family-name:var(--font-display)] text-[15px] leading-none tracking-[0.06em] text-gray-500 uppercase">
            Who
          </span>

          <div ref={whoList}>
            {!token ? (
              <button
                type="button"
                onClick={signInAndReturn}
                className="flex w-full items-center gap-3 py-1 text-left"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white" />
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-500">
                  <UserPlus className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <span className="text-sm font-bold text-[#1c2b39]">Add yourself</span>
              </button>
            ) : members === null ? (
              <div className="flex flex-col gap-3" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-11 animate-pulse rounded-2xl bg-gray-100" />
                ))}
              </div>
            ) : (
              <ul className="-my-1 divide-y divide-gray-100">
                {members.map((m) => {
                  const on = selected.includes(m.contactId);
                  const relation = m.isSelf ? 'You' : (m.relationship ?? null);
                  const held = roles.filter((o) => holds(mine, m.contactId, o.id));
                  return (
                    <li key={m.contactId}>
                      <button
                        type="button"
                        onClick={() => toggle(m.contactId)}
                        aria-pressed={on}
                        className="flex w-full items-start gap-3 py-3 text-left"
                      >
                        <span
                          className={`mt-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                            on
                              ? 'border-[var(--accent,#299c8f)] bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                        </span>
                        <Avatar name={m.name} imageUrl={m.imageUrl} size={36} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-1.5">
                            <span className="truncate text-sm font-bold text-[#1c2b39]">
                              {m.name}
                            </span>
                            {relation && (
                              <span className="shrink-0 text-[0.7rem] font-semibold text-gray-500">
                                {relation}
                              </span>
                            )}
                          </span>
                          <span className="mt-1 flex flex-wrap gap-1">
                            {/* Each way in once: a plain pill until it is done,
                            then the same pill ticked (a volunteer's, as the
                            roles they hold). */}
                            {roles.length > 0 &&
                              held.length === 0 &&
                              (teams.length ? (
                                // Three, then "+3": six teams per person wrapped
                                // each card to four lines.
                                <>
                                  {teams.slice(0, 3).map((t) => (
                                    <Pill key={t.id}>{t.name}</Pill>
                                  ))}
                                  {teams.length > 3 && <Pill>+{teams.length - 3}</Pill>}
                                </>
                              ) : (
                                <Pill>Volunteer</Pill>
                              ))}
                            {hasCare &&
                              careName &&
                              (householdBox ? (
                                <DonePill>{careName}</DonePill>
                              ) : (
                                <Pill>{careName}</Pill>
                              ))}
                            {held.map((o) => (
                              <span
                                key={o.id}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--accent-soft,#e6f4f2)] px-2 py-0.5 text-[0.65rem] font-bold text-[var(--accent-text,var(--accent,#299c8f))]"
                              >
                                <Check className="h-2.5 w-2.5" strokeWidth={3} />
                                {roleName(o, collection.title)}
                              </span>
                            ))}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Somebody ticked: Next, as on an event. Nobody (or signed out): the
          sheet asks what brings them, so somebody who needs a box, and is not
          signed in, gets there in one tap too.

          Once somebody is ticked it docks to the bottom of the window until
          its own place comes into view, as the event page's Next does, so a
          big household never scrolls to find it (Colton, 2026-10-16). By
          hand, because position: sticky cannot work on this site; see
          lib/bottom-dock.ts. */}
          <div ref={dockSlot} className="-mb-5" style={dock ? { height: dock.height } : undefined}>
            <div
              className={
                dock
                  ? 'fixed bottom-0 z-40 bg-white px-5 pt-3 pb-5 shadow-[0_-10px_24px_-18px_rgba(16,24,32,0.45)]'
                  : 'relative -mx-5 rounded-b-2xl bg-white px-5 pt-1 pb-5'
              }
              style={dock ? { left: dock.left, width: dock.width } : undefined}
            >
              {dock && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-full h-6 bg-gradient-to-t from-white to-transparent"
                />
              )}
              <button
                type="button"
                onClick={() =>
                  setStep(chosen.length ? (hasCare || teams.length ? 'way' : 'help') : 'choose')
                }
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[var(--accent,#299c8f)] px-6 py-3 text-base font-bold text-[var(--on-accent,#fff)] shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
              >
                {chosen.length ? 'Next' : 'Get started'}
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </>
      )}

      <SignUpSheet
        step={step}
        setStep={setStep}
        token={token}
        members={members}
        selected={selected}
        toggle={toggle}
        startRole={startRole}
        clearStartRole={() => setStartRole(null)}
        defaultCampusId={defaultCampusId}
        pickFirst={pickFirst}
        openKey={preset?.side ? preset.nonce : undefined}
        apiBaseUrl={apiBaseUrl}
        collection={collection}
        roles={roles}
        roleNames={roleNames}
        mine={mine}
        careOffers={careOffers}
        careLabel={careLabel ?? null}
        careName={careName}
        accentStyle={accentStyle}
        onSent={onSent}
        onDone={() => setSelected([])}
        onBoxed={onBoxed}
        householdBox={householdBox}
      />

      {boxDone &&
        createPortal(
          <div style={accentStyle}>
            <RegisteredCurtain
              headline="You're all set."
              names={boxDone.names}
              title={boxDone.title}
              when={boxDone.when}
              image={collection.imageUrl ?? null}
              where={boxDone.place}
              target={() => whoList.current}
              onDone={() => setBoxDone(null)}
            />
          </div>,
          overlayHost ?? document.body
        )}
    </>
  );
}

/** The household's box in this Collection */
interface HouseholdBox {
  offerId: number;
  place: string | null;
}

function DonePill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--accent-soft,#e6f4f2)] px-2 py-0.5 text-[0.65rem] font-bold text-[var(--accent-text,var(--accent,#299c8f))]">
      <Check className="h-2.5 w-2.5" strokeWidth={3} />
      {children}
    </span>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[0.65rem] font-bold text-gray-700">
      {children}
    </span>
  );
}

function SignUpSheet({
  step,
  setStep,
  token,
  members,
  selected,
  toggle,
  startRole,
  clearStartRole,
  defaultCampusId,
  pickFirst,
  openKey,
  apiBaseUrl,
  collection,
  roles,
  roleNames,
  mine,
  careOffers,
  careLabel,
  careName,
  accentStyle,
  onSent,
  onDone,
  onBoxed,
  householdBox,
}: {
  step: Step | null;
  setStep: (s: Step | null) => void;
  token: string | null;
  members: HouseholdMember[] | null;
  selected: number[];
  toggle: (contactId: number) => void;
  startRole: number | null;
  clearStartRole: () => void;
  defaultCampusId: number | null;
  /** Opened on who is helping (a page's Serve link), as the Volunteer choice does */
  pickFirst: boolean;
  /** Changes on each page-link open, so a second link starts fresh even if the sheet is open */
  openKey?: number;
  apiBaseUrl: string;
  collection: FinderOpportunity;
  roles: FinderOpportunity[];
  roleNames: string[];
  mine: MyServeResponse[];
  careOffers: CareOffer[];
  careLabel: string | null;
  careName: string | null;
  accentStyle?: React.CSSProperties;
  onSent: () => void;
  onDone: () => void;
  /** A box was asked for: the sheet closes and the card shows it */
  onBoxed: (done: CareDone) => void;
  householdBox: HouseholdBox | null;
}) {
  const open = step !== null;
  // Per person: their way in, their campus, their roles, and each role's answers.
  const [way, setWay] = useState<Record<number, Way>>({});
  const [campusOf, setCampusOf] = useState<Record<number, number>>({});
  const [picks, setPicks] = useState<Record<number, number[]>>({});
  // The last shift taken off on the review step, so it can be put back.
  const [undo, setUndo] = useState<{ contactId: number; id: number; at: number } | null>(null);
  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo(null), 6000);
    return () => clearTimeout(t);
  }, [undo]);
  // Held shifts (signed up before) to drop on this save, per person.
  const [drops, setDrops] = useState<Record<number, number[]>>({});
  const toggleDrop = (contactId: number, id: number) =>
    setDrops((cur) => {
      const list = cur[contactId] ?? [];
      return {
        ...cur,
        [contactId]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
      };
    });
  const responseFor = (contactId: number, roleId: number) =>
    mine.find(
      (r) =>
        r.contactId === contactId &&
        r.opportunityId === roleId &&
        (r.status === 'pending' || r.status === 'placed')
    )?.id;
  // Online changes stay open until changesClose (serve-changes-1.0.sql), else the shift.
  const changeable = (roleId: number) => {
    const o = roles.find((r) => r.id === roleId);
    const until = o?.changesClose ?? o?.date;
    return !until || parseMp(until).getTime() > Date.now();
  };
  const unpick = (contactId: number, id: number) => {
    const at = (picks[contactId] ?? []).indexOf(id);
    setPicks((cur) => ({ ...cur, [contactId]: (cur[contactId] ?? []).filter((x) => x !== id) }));
    setUndo({ contactId, id, at });
  };
  const repick = () => {
    if (!undo) return;
    setPicks((cur) => {
      const list = (cur[undo.contactId] ?? []).filter((x) => x !== undo.id);
      list.splice(Math.max(0, undo.at), 0, undo.id);
      return { ...cur, [undo.contactId]: list };
    });
    setUndo(null);
  };
  // The team each person chose on the way step, where the roles step opens.
  const [teamFor, setTeamFor] = useState<Record<number, number[]>>({});
  // Teams are a multiselect: ticking any makes the person a volunteer, and
  // the next step shows every ticked team's tables, one after another.
  const setTeams = (contactId: number, ids: number[]) => {
    setTeamFor((cur) => ({ ...cur, [contactId]: ids }));
    setWay((cur) => {
      const next = { ...cur };
      if (ids.length) next[contactId] = 'help';
      else if (next[contactId] === 'help') delete next[contactId];
      return next;
    });
  };
  const teams = useMemo(() => (hasTeams(roles) ? teamSummaries(roles) : []), [roles]);

  // CPP (serve-cpp-1.0.sql): who in the household is cleared, read once the
  // sheet opens on a collection with CPP roles, and who has asked for help.
  const needsCpp = roles.some((o) => o.cppRequired);
  const [cleared, setCleared] = useState<Set<number> | null>(null);
  const [cppAsk, setCppAsk] = useState<
    Record<number, { asked: boolean; sending: boolean; error: string | null }>
  >({});
  useEffect(() => {
    if (!open || !needsCpp || !token || cleared) return;
    fetchCppCleared(apiBaseUrl)
      .then(setCleared)
      .catch(() => setCleared(new Set()));
  }, [open, needsCpp, token, cleared, apiBaseUrl]);
  const askCpp = (contactId: number, ids: number[]) => {
    setCppAsk((cur) => ({ ...cur, [contactId]: { asked: false, sending: true, error: null } }));
    askAboutCpp(apiBaseUrl, { contactId, opportunityIds: ids })
      .then(() =>
        setCppAsk((cur) => ({ ...cur, [contactId]: { asked: true, sending: false, error: null } }))
      )
      .catch((err: unknown) =>
        setCppAsk((cur) => ({
          ...cur,
          [contactId]: {
            asked: false,
            sending: false,
            error: err instanceof Error ? err.message : 'Could not send it',
          },
        }))
      );
  };
  const [answers, setAnswers] = useState<Record<string, FormAnswers>>({});
  const [forms, setForms] = useState<Record<number, ServeForm | null>>({});
  const [index, setIndex] = useState(0);
  const [comments, setComments] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ name: string; role: string; dropped?: boolean }[] | null>(
    null
  );
  const [campusOpen, setCampusOpen] = useState(false);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  // Reached from "I want to help" with nobody ticked: tick them here first.
  const [picking, setPicking] = useState(false);

  const campuses = useMemo(() => {
    const ids = [...new Set(roles.map((o) => o.congregationId))].filter((id) => id !== CHURCH_WIDE);
    return ids
      .map((id) => ({
        id,
        name: campusLabel(id, roles.find((o) => o.congregationId === id)?.congregationName ?? ''),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [roles]);
  const offered = (id: number | null | undefined) =>
    id != null && campuses.some((c) => c.id === id);
  const startCampus = offered(defaultCampusId) ? defaultCampusId : (campuses[0]?.id ?? null);
  // Each person starts at their own campus when it has a role here, else the visitor's.
  const campusFor = (m: HouseholdMember) =>
    campusOf[m.contactId] ?? (offered(m.campusId) ? m.campusId! : startCampus);

  const ticked = (members ?? []).filter((m) => selected.includes(m.contactId));
  const careFor = (m: HouseholdMember) => !!careLabel && way[m.contactId] === 'care';
  const volunteers = ticked.filter((m) => !careFor(m));
  const wantsBox = ticked.some(careFor);
  // Receive a box is only offered once a place takes requests, and never to a
  // household that already has one; otherwise it says why ("Opens Sun, Oct 25",
  // "Already signed up · Lake Orion") instead of walking anyone through to "no".
  const careOpen = !householdBox && careOffers.some((o) => o.state === 'open' || !!o.externalUrl);
  const opensOn = careOffers
    .filter((o) => o.state === 'upcoming' && o.registrationStart)
    .map((o) => o.registrationStart!)
    .sort()[0];
  const careNote = careOpen
    ? undefined
    : householdBox
      ? `Already signed up${householdBox.place ? ` · ${householdBox.place}` : ''}`
      : opensOn
        ? `Opens ${fmtDay(parseMp(opensOn))}`
        : 'Not taking requests';
  const withForms = volunteers.flatMap((m) =>
    (picks[m.contactId] ?? [])
      .map((id) => roles.find((o) => o.id === id))
      .filter((o): o is FinderOpportunity => !!o && !!o.customFormId)
      .map((o) => ({ m, o }))
  );

  // Fresh each time it opens; a role tapped on the page goes straight on.
  useEffect(() => {
    if (!open) {
      setPicking(false);
      return;
    }
    setPicking(pickFirst);
    setSent(null);
    setError(null);
    setIndex(0);
    setCampusOpen(false);
    if (startRole && members) {
      const self = members.find((m) => m.isSelf);
      const role = roles.find((o) => o.id === startRole);
      if (self && role) {
        setWay((cur) => ({ ...cur, [self.contactId]: 'help' }));
        if (role.congregationId !== CHURCH_WIDE)
          setCampusOf((cur) => ({ ...cur, [self.contactId]: role.congregationId }));
        setPicks((cur) => ({ ...cur, [self.contactId]: [role.id] }));
      }
      clearStartRole();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openKey]);

  // Each picked role's questions, once.
  useEffect(() => {
    for (const { o } of withForms) {
      if (forms[o.id] !== undefined) continue;
      setForms((cur) => ({ ...cur, [o.id]: null }));
      fetchForm(apiBaseUrl, o.id)
        .then((f) => setForms((cur) => ({ ...cur, [o.id]: f })))
        .catch(() => {});
    }
  }, [withForms, forms, apiBaseUrl]);

  // The steps for this visit: the rail, and what Back and Next walk.
  const steps: Step[] = picking
    ? ['who', 'help', ...(withForms.length ? ['details' as Step] : []), 'review']
    : step === 'box' && !wantsBox
      ? ['box']
      : [
          ...((careLabel || teams.length) && ticked.length ? ['way' as Step] : []),
          ...(volunteers.length || !careLabel ? ['help' as Step] : []),
          ...(withForms.length ? ['details' as Step] : []),
          ...(volunteers.length ? ['review' as Step] : []),
          ...(wantsBox ? ['box' as Step] : []),
        ];
  const at = step ? steps.indexOf(step === 'done' ? 'review' : step) : -1;
  // Which list this step walks, one person (or one form) per page.
  const pages =
    step === 'way' ? ticked : step === 'help' ? volunteers : step === 'details' ? withForms : [];
  const person =
    step === 'way' ? ticked[index] : step === 'help' ? volunteers[index] : withForms[index]?.m;

  const close = () => {
    if (sent || step === 'box') {
      onDone();
      setWay({});
      setTeamFor({});
      setPicks({});
      setDrops({});
      setAnswers({});
    }
    setStep(null);
  };
  const go = (s: Step, i = 0) => {
    setIndex(i);
    setCampusOpen(false);
    setStep(s);
  };
  const forward = () => {
    if (index < pages.length - 1) return go(step!, index + 1);
    const next = steps[at + 1];
    if (next) go(next);
  };
  const back = () => {
    if (index > 0) return go(step!, index - 1);
    const prev = steps[at - 1];
    if (prev) {
      const n =
        prev === 'way'
          ? ticked.length
          : prev === 'help'
            ? volunteers.length
            : prev === 'details'
              ? withForms.length
              : 1;
      go(prev, Math.max(0, n - 1));
    } else if (picking || step === 'box') {
      setPicking(false);
      setStep('choose');
    }
  };

  // Is this page answered, and if not, what is missing (said beside Next).
  const missing = (() => {
    if (step === 'way')
      // A ticked team counts even if `way` was cleared under it (2026-10-16:
      // reopening after a sign-up showed the team ticked, Continue dead).
      return person && (way[person.contactId] || (teamFor[person.contactId] ?? []).length)
        ? null
        : teams.length
          ? 'Pick a team'
          : 'Pick one';
    if (step === 'who') return ticked.length ? null : 'Pick someone';
    if (step === 'help') {
      if (!person) return null;
      return (picks[person.contactId] ?? []).length || (drops[person.contactId] ?? []).length
        ? null
        : 'Pick a role';
    }
    if (step === 'details') {
      const item = withForms[index];
      const form = item ? forms[item.o.id] : null;
      if (!item || !form) return null;
      return missingRequired(form, answers[`${item.m.contactId}:${item.o.id}`] ?? {}).length
        ? 'Answer the questions marked required'
        : null;
    }
    if (step === 'review')
      return volunteers.some(
        (m) => (picks[m.contactId] ?? []).length || (drops[m.contactId] ?? []).length
      )
        ? null
        : 'Pick a role';
    return null;
  })();

  const nextLabel = (() => {
    if (index < pages.length - 1) {
      const nextPerson =
        step === 'details' ? withForms[index + 1]?.m : (pages[index + 1] as HouseholdMember);
      return nextPerson ? `Next: ${firstName(nextPerson)}` : 'Next';
    }
    return 'Continue';
  })();

  const send = async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    const done: { name: string; role: string; dropped?: boolean }[] = [];
    try {
      // Every (person, role), grouped by role; a role with a form goes per person.
      const byRole = new Map<number, HouseholdMember[]>();
      for (const m of volunteers)
        for (const id of picks[m.contactId] ?? []) byRole.set(id, [...(byRole.get(id) ?? []), m]);
      // A few roles at a time: one after another took ~15 s for a handful.
      const jobs = [...byRole].flatMap(([roleId, people]) => {
        const role = roles.find((o) => o.id === roleId)!;
        const batches = role.customFormId ? people.map((p) => [p]) : [people];
        return batches.map((batch) => async () => {
          await submitResponse(apiBaseUrl, roleId, {
            people: batch.map((p) => ({ contactId: p.contactId })),
            comments: comments.trim(),
            answers: role.customFormId ? answers[`${batch[0].contactId}:${roleId}`] : undefined,
          });
          for (const p of batch)
            done.push({ name: p.name, role: roleName(role, collection.title) });
        });
      });
      // The first alone: it gives anyone without a Participant record one,
      // which parallel requests would each do.
      const queue = jobs.slice();
      if (queue.length) await queue.shift()!();
      let failed: unknown = null;
      await Promise.all(
        Array.from({ length: Math.min(4, queue.length) }, async () => {
          while (queue.length && !failed) {
            try {
              await queue.shift()!();
            } catch (err) {
              failed = err;
            }
          }
        })
      );
      if (failed) throw failed;
      // Then the drops: after the new shifts are safely in, so a swap never
      // leaves someone with neither.
      const dropping = volunteers.flatMap((m) =>
        (drops[m.contactId] ?? [])
          .map((id) => ({ m, id, responseId: responseFor(m.contactId, id) }))
          .filter((d) => d.responseId)
      );
      if (dropping.length) {
        await cancelShifts(apiBaseUrl, {
          responseIds: dropping.map((d) => d.responseId!),
          added: [...byRole.keys()],
        });
        for (const d of dropping) {
          const role = roles.find((o) => o.id === d.id);
          done.push({
            name: d.m.name,
            role: role ? roleName(role, collection.title) : 'Shift',
            dropped: true,
          });
        }
        setDrops({});
      }
      setSent(done);
      setComments('');
      onSent();
      setStep('done');
      void confirmServe(apiBaseUrl, {
        collectionId: collection.id,
        people: volunteers
          .map((m) => ({
            contactId: m.contactId,
            added: picks[m.contactId] ?? [],
            dropped: dropping.filter((d) => d.m.contactId === m.contactId).map((d) => d.id),
          }))
          .filter((p) => p.added.length || p.dropped.length),
      });
    } catch (err) {
      if (done.length) {
        setSent(done);
        onSent();
        setStep('done');
      }
      // The sign-in ran out and MP would not renew it: open the sign-in, not
      // an error. Their picks stay on the page for the second press.
      if (err instanceof ResponseError && err.status === 401) signInAndReturn();
      else setError(err instanceof Error ? err.message : 'Could not send that right now.');
    } finally {
      setSending(false);
    }
  };

  const head = (m: HouseholdMember, title: string, sub?: string) => (
    <div className="flex items-center gap-3 pb-1">
      <Avatar name={m.name} imageUrl={m.imageUrl} size={40} />
      <div className="min-w-0 flex-1">
        <p className="text-base font-bold text-[#1c2b39]">{title}</p>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
      </div>
    </div>
  );
  const nOf = (n: number) => (pages.length > 1 ? `${n + 1} of ${pages.length}` : undefined);

  const footer = (() => {
    if (!step || step === 'choose') return null;
    if (step === 'done')
      return (
        <button
          type="button"
          onClick={() => (wantsBox ? go('box') : close())}
          className="btn btn-primary w-full"
        >
          {wantsBox ? `Next: ${careName}` : 'Done'}
          {wantsBox && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
        </button>
      );
    const backBtn = (at > 0 || index > 0 || picking || (step === 'box' && !wantsBox)) && (
      <button type="button" onClick={back} className="btn btn-quiet shrink-0">
        Back
      </button>
    );
    if (step === 'box')
      return (
        <div className="flex items-center gap-2">
          {backBtn}
          {/* The care card draws its Request button here. */}
          <div ref={setSlot} className="min-w-0 flex-1" />
        </div>
      );
    if (step === 'who' && !token) return null;
    return (
      <div className="flex items-center gap-2">
        {backBtn}
        <div className="ml-auto flex min-w-0 items-center gap-3">
          {missing && (
            <span className="max-w-[14rem] min-w-0 truncate text-xs font-semibold text-gray-500">
              {missing}
            </span>
          )}
          {step === 'review' ? (
            <button
              type="button"
              onClick={() => void send()}
              disabled={sending || !!missing}
              className="btn btn-primary shrink-0"
            >
              {sending
                ? 'One moment…'
                : volunteers.some((m) => (drops[m.contactId] ?? []).length)
                  ? 'Save changes'
                  : 'Sign up'}
              {!sending && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
            </button>
          ) : (
            <button
              type="button"
              disabled={!!missing}
              onClick={forward}
              className="btn btn-primary shrink-0"
            >
              {nextLabel}
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
    );
  })();

  const campusPicker = (m: HouseholdMember) => {
    const here = campusFor(m);
    const name = campuses.find((c) => c.id === here)?.name ?? 'Pick a campus';
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => setCampusOpen((v) => !v)}
          className="field flex items-center gap-3 text-left"
          aria-expanded={campusOpen}
        >
          {here != null && <CampusIcon congregationId={here} className="h-7 w-7 shrink-0" />}
          <span className="min-w-0 flex-1 truncate">{name}</span>
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </button>
        {campusOpen && (
          <div className="card mt-2 max-h-72 overflow-y-auto p-1.5">
            {campuses.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setCampusOf((cur) => ({ ...cur, [m.contactId]: c.id }));
                  setPicks((cur) => ({ ...cur, [m.contactId]: [] }));
                  setCampusOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition hover:bg-[var(--color-teal-soft)] ${
                  c.id === here ? 'bg-[var(--color-teal-soft)]' : ''
                }`}
              >
                <CampusIcon congregationId={c.id} className="h-7 w-7 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                {c.id === here && <Check className="h-4 w-4" strokeWidth={3} />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // A role with an External_Registration_URL (Pontiac's, in the Dream
  // Center's own MP) is signed up for there: a link after this campus's own
  // roles, never a pick here.
  const shiftRows = (m: HouseholdMember) => {
    const here = campusFor(m);
    const partner = roles.filter(
      (o) =>
        o.externalRegistrationUrl && (o.congregationId === here || o.congregationId === CHURCH_WIDE)
    );
    if (!partner.length) return ownRows(m);
    const own = ownRows(m, true);
    return (
      <div className="space-y-4">
        {own}
        <ul className="space-y-2.5">
          {partner.map((o) => (
            <li key={o.id}>
              <a
                href={o.externalRegistrationUrl!}
                target="_blank"
                rel="noopener"
                className={`${ROW} no-underline`}
              >
                <span className="min-w-0 flex-1">
                  <b className="block text-sm text-[#1c2b39]">{roleName(o, collection.title)}</b>
                  {o.date && (
                    <span className="block text-xs text-gray-500">{fmtDay(parseMp(o.date))}</span>
                  )}
                </span>
                <span className="inline-flex shrink-0 items-center gap-1 text-sm font-bold text-[var(--accent-text,var(--accent,#299c8f))]">
                  Sign up
                  <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const ownRows = (m: HouseholdMember, partnerToo = false) => {
    const here = campusFor(m);
    const atCampus = roles.filter(
      (o) =>
        !o.externalRegistrationUrl &&
        (o.congregationId === here || o.congregationId === CHURCH_WIDE)
    );
    // Grouped by day, the way an event's shifts are grouped by time.
    const days = new Map<string, FinderOpportunity[]>();
    for (const o of atCampus) {
      const k = o.date ? fmtDay(parseMp(o.date)) : 'Any time';
      days.set(k, [...(days.get(k) ?? []), o]);
    }
    const mineHere = picks[m.contactId] ?? [];
    if (!atCampus.length)
      return partnerToo ? null : (
        <p className="text-sm text-gray-500">Nothing open here right now.</p>
      );
    const toggle = (id: number) =>
      setPicks((cur) => {
        const list = cur[m.contactId] ?? [];
        return {
          ...cur,
          [m.contactId]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
        };
      });
    // Roles with a Serve Team: the day, then the team, then a role-by-service grid.
    if (hasTeams(atCampus))
      return (
        <TeamPicker
          key={`${m.contactId}:${here}:${(teamFor[m.contactId] ?? []).join(',')}`}
          roles={atCampus}
          teamIds={teamFor[m.contactId] ?? []}
          cppOk={!needsCpp || !!cleared?.has(m.contactId)}
          cpp={
            // Locked while the check loads, but no "Let Caleb know" until it
            // has answered: a cleared person never sees the line flash.
            needsCpp && cleared
              ? {
                  ...(cppAsk[m.contactId] ?? { asked: false, sending: false, error: null }),
                  ask: (ids) => askCpp(m.contactId, ids),
                }
              : null
          }
          label={(o) =>
            roleName(o, collection.title)
              .split(' · ')
              .filter((part) => !/\d{1,2}\/\d{1,2}/.test(part))
              .join(' · ') || roleName(o, collection.title)
          }
          picked={mineHere}
          holds={(id) => holds(mine, m.contactId, id)}
          onToggle={toggle}
          dropped={drops[m.contactId] ?? []}
          onDrop={(id) => toggleDrop(m.contactId, id)}
          changeable={changeable}
        />
      );
    return (
      <div className="space-y-4">
        {[...days.entries()].map(([day, group]) => (
          <div key={day}>
            {days.size > 1 && <div className="kicker mb-1.5">{day}</div>}
            <div className="grid gap-1.5">
              {group.map((o) => {
                const has = holds(mine, m.contactId, o.id);
                const on = mineHere.includes(o.id) || has;
                const off = has || (o.remaining === 0 && !on);
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={off}
                    onClick={() =>
                      setPicks((cur) => {
                        const list = cur[m.contactId] ?? [];
                        return {
                          ...cur,
                          [m.contactId]: list.includes(o.id)
                            ? list.filter((id) => id !== o.id)
                            : [...list, o.id],
                        };
                      })
                    }
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-[border-color,background-color,box-shadow] ${
                      on && !has
                        ? 'border-[var(--color-teal)] bg-[var(--color-teal-soft)]'
                        : off
                          ? 'border-[var(--color-line)] bg-white opacity-55'
                          : 'border-[var(--color-line)] bg-white hover:shadow-md'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                        on
                          ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-[var(--on-accent,#fff)]'
                          : 'border-[var(--color-line)] bg-white'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-[#1c2b39]">
                        {roleName(o, collection.title)}
                      </span>
                      {serveShift(o) && (
                        <span className="block text-xs text-gray-500">{serveShift(o)}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-gray-500">
                      {has
                        ? 'Signed up'
                        : o.remaining === 0
                          ? 'Full'
                          : o.remaining != null
                            ? `${o.remaining} left`
                            : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const subtitle =
    step === 'box'
      ? careLabel
      : [...roles.map((o) => o.date), ...careOffers.map((o) => o.date)]
          .filter((d): d is string => Boolean(d))
          .sort()
          .filter((_, i, a) => i === 0 || i === a.length - 1)
          .map((d) => fmtDay(parseMp(d)))
          .filter((d, i, a) => a.indexOf(d) === i)
          .join(' to ');

  return (
    <ResponsiveSheet
      open={open}
      onClose={close}
      maxWidth="max-w-3xl"
      noPanelPadding
      panelClassName="bg-ground text-ink md:rounded-3xl"
      hideCloseButton
      footer={
        footer && (
          <div
            className="border-t border-[var(--color-line)]/60 bg-white/90 px-5 py-4 backdrop-blur"
            style={accentStyle}
          >
            {footer}
          </div>
        )
      }
      header={() => (
        /* The event registration sheet's band, with its step rail. */
        <div
          className="relative overflow-hidden bg-[color-mix(in_srgb,var(--accent,#1c2b39)_45%,#1c2b39)] text-white"
          style={accentStyle}
        >
          {collection.imageUrl && (
            <>
              <img
                src={collection.imageUrl}
                alt=""
                className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover blur-2xl"
              />
              <div className="absolute inset-0 bg-black/45" />
            </>
          )}
          <div className="relative flex items-center gap-3 px-5 pt-5 pb-3">
            {collection.imageUrl && (
              <img
                src={collection.imageUrl}
                alt=""
                className="aspect-video w-20 shrink-0 rounded-xl object-cover shadow-lg ring-1 ring-white/20"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-base font-extrabold">{collection.title}</div>
              {subtitle && <div className="truncate text-xs text-white/70">{subtitle}</div>}
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={close}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {steps.length > 1 && step !== 'choose' && (
            <ol className="relative flex gap-1.5 px-5 pb-4">
              {steps.map((s, i) => (
                <li key={s} className="min-w-0 flex-1">
                  <div
                    className={`h-1 rounded-full transition ${i <= at ? 'bg-[var(--accent,#299c8f)]' : 'bg-white/25'}`}
                  />
                  <div
                    className={`mt-1.5 truncate font-[family-name:var(--font-display)] text-[0.8rem] tracking-[0.06em] uppercase ${
                      s === steps[at] ? 'text-white' : 'text-white/50'
                    }`}
                  >
                    {s === 'box' && careName ? careName : STEP_NAME[s as keyof typeof STEP_NAME]}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    >
      <SheetPage name="main">
        <div className="space-y-4 p-5 pb-8 text-[#1c2b39] sm:p-6" style={accentStyle}>
          {step === 'choose' && (
            <div className="grid gap-2.5">
              <ChoiceCard
                icon={<HandHeart className="h-6 w-6" />}
                title="Volunteer"
                pills={teams.length ? teams.map((t) => t.name) : roleNames}
                onClick={() => {
                  setPicking(true);
                  setStep('who');
                }}
              />
              {careLabel && (
                <ChoiceCard
                  icon={<Package className="h-6 w-6" />}
                  title={careName ?? careLabel}
                  note={careNote}
                  disabled={!careOpen}
                  onClick={() => setStep('box')}
                />
              )}
            </div>
          )}

          {step === 'way' && person && (
            <div className="space-y-2.5">
              {head(
                person,
                teams.length
                  ? `How is ${firstName(person)} helping?`
                  : `How is ${firstName(person)} taking part?`,
                nOf(index)
              )}
              {teams.length > 0 &&
                (() => {
                  const mineTeams = teamFor[person.contactId] ?? [];
                  const all = teams.every((t) => mineTeams.includes(t.id));
                  const box = (on: boolean) => (
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition ${
                        on
                          ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-[var(--on-accent,#fff)]'
                          : 'border-[var(--color-line)] bg-white'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                  );
                  return (
                    <div className="grid gap-2.5">
                      {/* A link, not a seventh card: as a card it read as a team. */}
                      {teams.length > 1 && (
                        <div className="-mt-1 flex justify-end">
                          <button
                            type="button"
                            aria-pressed={all}
                            onClick={() =>
                              setTeams(person.contactId, all ? [] : teams.map((t) => t.id))
                            }
                            className="text-sm font-semibold text-[var(--accent-text,var(--color-teal))] hover:underline"
                          >
                            {all ? 'Clear' : 'Select all'}
                          </button>
                        </div>
                      )}
                      {teams.map((t) => {
                        const on = mineTeams.includes(t.id);
                        const n = (picks[person.contactId] ?? []).filter((id) =>
                          t.roleIds.includes(id)
                        ).length;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            aria-pressed={on}
                            onClick={() =>
                              setTeams(
                                person.contactId,
                                on
                                  ? mineTeams.filter((id) => id !== t.id)
                                  : teams
                                      .map((x) => x.id)
                                      .filter((id) => id === t.id || mineTeams.includes(id))
                              )
                            }
                            className={`${ROW} ${on ? '!border-[var(--color-teal)] !bg-[var(--color-teal-soft)] ring-2 ring-[var(--color-teal)]' : 'hover:border-[var(--color-teal)]/40'}`}
                          >
                            {box(on)}
                            <span className="min-w-0 flex-1">
                              <span className="block text-base font-bold text-[#1c2b39]">
                                {t.name}
                              </span>
                              <span className="block text-xs text-gray-500">
                                {t.when}
                                {t.description ? ` · ${t.description}` : ''}
                              </span>
                            </span>
                            <span className="shrink-0 text-right text-xs font-semibold text-gray-500">
                              {n > 0 ? `${n} picked` : t.open > 0 ? `${t.open} open` : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              <div className="grid auto-rows-fr gap-2.5">
                {(teams.length
                  ? careLabel
                    ? (['care'] as const)
                    : []
                  : (['help', 'care'] as const)
                ).map((w) => {
                  const on = way[person.contactId] === w;
                  const shut = w === 'care' && !careOpen;
                  return (
                    <button
                      key={w}
                      type="button"
                      disabled={shut}
                      onClick={() => {
                        setWay((cur) => ({ ...cur, [person.contactId]: w }));
                        if (w === 'care') setTeamFor((cur) => ({ ...cur, [person.contactId]: [] }));
                        // The tick lands, then the next person slides in.
                        window.setTimeout(() => {
                          if (index < ticked.length - 1) go('way', index + 1);
                        }, 220);
                      }}
                      className={`${ROW} disabled:cursor-default disabled:opacity-55 ${on ? '!border-[var(--color-teal)] !bg-[var(--color-teal-soft)] ring-2 ring-[var(--color-teal)]' : shut ? '' : 'hover:border-[var(--color-teal)]/40'}`}
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition ${
                          on
                            ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-[var(--on-accent,#fff)]'
                            : 'border-[var(--color-line)] bg-white'
                        }`}
                      >
                        {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-base font-bold text-[#1c2b39]">
                          {w === 'help' ? 'Volunteer' : careName}
                        </span>
                        {w === 'help' && roleNames.length > 0 && <RolePills names={roleNames} />}
                        {shut && careNote && (
                          <span className="mt-0.5 block text-sm font-semibold text-gray-500">
                            {careNote}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'who' && !token && (
            <div className="space-y-3">
              <p className="text-base font-bold">Who?</p>
              <button type="button" onClick={signInAndReturn} className="btn btn-primary">
                Sign in
                <LogIn className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          )}

          {step === 'who' && token && (
            <div className="space-y-2.5">
              <p className="text-base font-bold">Who?</p>
              <ul className="card divide-y divide-gray-100 px-4">
                {(members ?? []).map((m) => (
                  <li key={m.contactId}>
                    <button
                      type="button"
                      onClick={() => {
                        toggle(m.contactId);
                        setWay((cur) => ({ ...cur, [m.contactId]: 'help' }));
                      }}
                      className="flex w-full items-center gap-3 py-3 text-left"
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                          selected.includes(m.contactId)
                            ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-[var(--on-accent,#fff)]'
                            : 'border-[var(--color-line)] bg-white'
                        }`}
                      >
                        {selected.includes(m.contactId) && (
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        )}
                      </span>
                      <Avatar name={m.name} imageUrl={m.imageUrl} size={32} />
                      <b className="text-sm">{m.name}</b>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step === 'help' && person && (
            <div className="space-y-4">
              {/* Sticks above the team and day headings: signing up a family,
                  whose shifts these are must never scroll away. */}
              <div
                className={`sticky top-0 z-30 -mt-5 sm:-mt-6 ${PERSON_BAR} ${STICK_BG} ${BLEED} flex items-center`}
              >
                {head(person, `What is ${firstName(person)} helping with?`, nOf(index))}
              </div>
              {campuses.length > 1 && campusPicker(person)}
              {shiftRows(person)}
            </div>
          )}

          {step === 'details' &&
            withForms[index] &&
            (() => {
              const { m, o } = withForms[index];
              const form = forms[o.id];
              const k = `${m.contactId}:${o.id}`;
              return (
                <div className="space-y-4">
                  {head(
                    m,
                    `A few questions for ${firstName(m)}`,
                    [nOf(index), roleName(o, collection.title)].filter(Boolean).join(' · ')
                  )}
                  {form === undefined || form === null ? (
                    <div className="h-24 animate-pulse rounded-2xl bg-gray-100" />
                  ) : form.supported ? (
                    <FormFields
                      form={form}
                      answers={answers[k] ?? {}}
                      onChange={(n) => setAnswers((cur) => ({ ...cur, [k]: n }))}
                    />
                  ) : (
                    <p className="text-sm text-gray-600">
                      This role needs a form we can't show here yet.
                    </p>
                  )}
                </div>
              );
            })()}

          {step === 'review' && (
            <div className="space-y-4">
              {/* Everything each person signed up for, by day: what, which
                  team, which service, and when to be there. */}
              {volunteers.map((m) => {
                const going = drops[m.contactId] ?? [];
                const theirs = [...(picks[m.contactId] ?? []), ...going]
                  .map((id) => roles.find((o) => o.id === id))
                  .filter((o): o is FinderOpportunity => !!o)
                  .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
                const days = [...new Set(theirs.map((o) => (o.date ?? '').slice(0, 10)))];
                const name = (o: FinderOpportunity) =>
                  roleName(o, collection.title)
                    .split(' · ')
                    .filter((part) => !/\d{1,2}\/\d{1,2}/.test(part))
                    .join(' · ') || roleName(o, collection.title);
                return (
                  <section key={m.contactId} className="card overflow-hidden">
                    <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
                      <Avatar name={m.name} imageUrl={m.imageUrl} size={36} />
                      <b className="min-w-0 flex-1 truncate text-sm">{m.name}</b>
                      <span className="shrink-0 text-xs font-semibold text-gray-500">
                        {(() => {
                          const adds = theirs.length - going.length;
                          return [
                            adds ? `${adds} ${adds === 1 ? 'shift' : 'shifts'}` : null,
                            going.length ? `${going.length} dropped` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ');
                        })()}
                      </span>
                    </div>
                    <AnimatePresence initial={false}>
                      {days.map((d) => (
                        <motion.div
                          key={d || 'any'}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: 'easeOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pt-3 pb-1.5">
                            <div className="kicker mb-1">
                              {d ? fmtDay(parseMp(`${d}T00:00:00`)) : 'Any time'}
                            </div>
                            <ul>
                              <AnimatePresence initial={false}>
                                {theirs
                                  .filter((o) => (o.date ?? '').slice(0, 10) === d)
                                  .map((o) => (
                                    <motion.li
                                      key={o.id}
                                      layout
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.2, ease: 'easeOut' }}
                                      className="overflow-hidden"
                                    >
                                      {(() => {
                                        const gone = going.includes(o.id);
                                        return (
                                          <div
                                            className={`flex items-center gap-3 py-1.5 ${gone ? 'text-gray-400' : ''}`}
                                          >
                                            <span
                                              className={`w-16 shrink-0 text-xs font-bold ${gone ? '' : 'text-[#1c2b39]'}`}
                                            >
                                              {o.addToEventStart
                                                ? `${serviceTime(o.addToEventStart)} service`
                                                : o.date
                                                  ? serviceTime(o.date)
                                                  : ''}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                              <span className="flex items-center gap-2">
                                                <b
                                                  className={`text-sm ${gone ? 'line-through decoration-red-400' : ''}`}
                                                >
                                                  {name(o)}
                                                </b>
                                                {gone && (
                                                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[0.65rem] font-bold text-red-600">
                                                    Dropping
                                                  </span>
                                                )}
                                              </span>
                                              <span className="block text-xs text-gray-500">
                                                {[
                                                  o.serveTeam?.name,
                                                  !gone && serveShift(o)
                                                    ? `Arrive ${serveShift(o)}`
                                                    : null,
                                                ]
                                                  .filter(Boolean)
                                                  .join(' · ')}
                                              </span>
                                            </span>
                                            {gone ? (
                                              <button
                                                type="button"
                                                onClick={() => toggleDrop(m.contactId, o.id)}
                                                className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-[var(--accent-text,#299c8f)] transition hover:scale-105 hover:bg-gray-50 hover:shadow-sm active:scale-95"
                                              >
                                                <RotateCcw
                                                  className="h-3.5 w-3.5"
                                                  strokeWidth={2.5}
                                                />
                                                Keep
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => unpick(m.contactId, o.id)}
                                                aria-label={`Remove ${name(o)} for ${firstName(m)}`}
                                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:scale-105 hover:bg-red-50 hover:text-red-600 active:scale-95"
                                              >
                                                <X className="h-4 w-4" strokeWidth={2.5} />
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </motion.li>
                                  ))}
                              </AnimatePresence>
                            </ul>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    {!theirs.length && (
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <span className="min-w-0 flex-1 text-sm text-gray-500">
                          Nothing for {firstName(m)}
                        </span>
                        <button
                          type="button"
                          onClick={() => go('help', volunteers.indexOf(m))}
                          className="shrink-0 text-sm font-bold text-[var(--accent-text,#299c8f)] hover:underline"
                        >
                          Pick a shift
                        </button>
                      </div>
                    )}
                    <AnimatePresence initial={false}>
                      {undo?.contactId === m.contactId && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="overflow-hidden"
                        >
                          <div className="flex items-center gap-3 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
                            <span className="min-w-0 flex-1 truncate text-xs text-gray-600">
                              Removed{' '}
                              <b className="text-[#1c2b39]">
                                {(() => {
                                  const o = roles.find((r) => r.id === undo.id);
                                  return o ? name(o) : 'shift';
                                })()}
                              </b>
                            </span>
                            <button
                              type="button"
                              onClick={repick}
                              className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-[var(--accent-text,#299c8f)] transition hover:scale-105 hover:bg-white hover:shadow-sm active:scale-95"
                            >
                              <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} />
                              Undo
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </section>
                );
              })}
              <label className="flex flex-col gap-2">
                <span className="kicker">
                  Anything the team should know?{' '}
                  <span className="font-normal tracking-normal normal-case">Optional</span>
                </span>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  className="field"
                />
              </label>
              {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
            </div>
          )}

          {step === 'done' && sent && (
            <div className="space-y-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]">
                <Check className="h-6 w-6" strokeWidth={3} />
              </span>
              <p className="text-lg font-extrabold">
                {sent.some((s) => !s.dropped) ? "You're on the list." : 'All set.'}
              </p>
              <ul className="card divide-y divide-gray-100">
                {sent.map((s) => (
                  <li
                    key={`${s.name}:${s.role}:${s.dropped ? 'x' : ''}`}
                    className="flex items-baseline gap-2 px-4 py-3 text-sm"
                  >
                    <span className={`min-w-0 flex-1 ${s.dropped ? 'text-gray-500' : ''}`}>
                      <b>{s.name}</b>{' '}
                      <span className={`text-gray-500 ${s.dropped ? 'line-through' : ''}`}>
                        · {s.role}
                      </span>
                    </span>
                    {s.dropped && (
                      <span className="shrink-0 text-xs font-semibold text-red-600">Dropped</span>
                    )}
                  </li>
                ))}
              </ul>
              {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
            </div>
          )}

          {step === 'box' && (
            <CarePanel
              apiBaseUrl={apiBaseUrl}
              offers={careOffers}
              accentStyle={accentStyle}
              embedded={{ submitSlot: slot }}
              people={ticked.filter(careFor).map((m) => m.contactId)}
              onDone={(done) => {
                close();
                onBoxed(done);
              }}
            />
          )}
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}

function RolePills({ names }: { names: string[] }) {
  return (
    <span className="mt-1.5 flex flex-wrap gap-1">
      {names.slice(0, 4).map((n) => (
        <span
          key={n}
          className="rounded-full bg-[var(--color-mist)] px-2 py-0.5 text-[0.68rem] font-semibold text-[var(--color-slate-deep)]"
        >
          {n}
        </span>
      ))}
      {names.length > 4 && (
        <span className="px-1 py-0.5 text-[0.68rem] font-semibold text-[var(--color-slate)]">
          +{names.length - 4}
        </span>
      )}
    </span>
  );
}

function ChoiceCard({
  icon,
  title,
  pills,
  note,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  pills?: string[];
  /** Why it cannot be picked yet ("Opens Sun, Oct 25") */
  note?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${ROW} disabled:cursor-default disabled:opacity-55 ${disabled ? '' : 'hover:border-[var(--color-teal)]/40'}`}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-teal-soft)] text-[var(--accent-text,var(--color-teal))]">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-bold text-[#1c2b39]">{title}</span>
        {pills && pills.length > 0 && <RolePills names={pills} />}
        {note && <span className="mt-0.5 block text-sm font-semibold text-gray-500">{note}</span>}
      </span>
      {!disabled && <ChevronRight className="h-5 w-5 text-gray-400" />}
    </button>
  );
}
